import React, { useEffect, useRef, useState } from 'react';
import './styles/SignUpForm.css';

// Normalize any error-like value into a human-readable string
function toMessage(value) {
  try {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.message || String(value);
    if (Array.isArray(value)) {
      // FastAPI 422-style error array
      return value
        .map(e => {
          const loc = Array.isArray(e?.loc) ? e.loc.join('.') : '';
          const msg = e?.msg || e?.message || JSON.stringify(e);
          return loc ? `${loc}: ${msg}` : msg;
        })
        .join('; ');
    }
    if (typeof value === 'object') {
      if (typeof value.message === 'string') return value.message;
      if (typeof value.detail === 'string') return value.detail;
      if (value.detail && typeof value.detail === 'object') {
        if (typeof value.detail.message === 'string') return value.detail.message;
        if (typeof value.detail.errorCode === 'string' && typeof value.detail.message === 'string') {
          return `${value.detail.errorCode}: ${value.detail.message}`;
        }
      }
      if (typeof value.error === 'string') return value.error;
      if (typeof value.error_description === 'string') return value.error_description;
      if (typeof value.errorCode === 'string' && typeof value.message === 'string') {
        return `${value.errorCode}: ${value.message}`;
      }
    }
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function tryParseJson(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function parseJsonDeep(value, maxDepth = 3) {
  let current = value;
  for (let i = 0; i < maxDepth; i += 1) {
    const parsed = tryParseJson(current);
    if (!parsed) break;
    current = parsed;
  }
  return current;
}

function getSignupErrorMessage(status, payload) {
  const parsedPayload = parseJsonDeep(payload);
  const parsedDetail = parseJsonDeep(parsedPayload?.detail);
  const wrapper = (parsedDetail && typeof parsedDetail === 'object') ? parsedDetail : parsedPayload;
  const nestedBody = parseJsonDeep(wrapper?.body);
  const scimDetail = nestedBody?.detail;
  const outerStatus = Number(status);
  const wrapperStatus = Number(wrapper?.status);
  const scimStatus = Number(nestedBody?.status);
  const looksLikeUniqueness =
    nestedBody?.scimType === 'uniqueness' || /already taken/i.test(String(scimDetail || ''));

  if (
    looksLikeUniqueness &&
    [outerStatus, wrapperStatus, scimStatus].some(code => code === 409)
  ) {
    return 'An account with this email already exists. Please log in or use a different email address.';
  }

  if (looksLikeUniqueness) {
    return 'An account with this email already exists. Please log in or use a different email address.';
  }

  return (
    toMessage(scimDetail) ||
    toMessage(wrapper?.detail) ||
    toMessage(parsedPayload?.detail) ||
    toMessage(parsedPayload) ||
    toMessage(payload) ||
    `Sign-up failed (HTTP ${status})`
  );
}

function isDuplicateEmailSignupError(status, payload) {
  const parsedPayload = parseJsonDeep(payload);
  const parsedDetail = parseJsonDeep(parsedPayload?.detail);
  const wrapper = (parsedDetail && typeof parsedDetail === 'object') ? parsedDetail : parsedPayload;
  const nestedBody = parseJsonDeep(wrapper?.body);
  const scimDetail = nestedBody?.detail;
  const outerStatus = Number(status);
  const wrapperStatus = Number(wrapper?.status);
  const scimStatus = Number(nestedBody?.status);
  const looksLikeUniqueness =
    nestedBody?.scimType === 'uniqueness' || /already taken/i.test(String(scimDetail || ''));

  return looksLikeUniqueness && [outerStatus, wrapperStatus, scimStatus].some(code => code === 409);
}

/**
 * Direct sign-up form.
 * Sends POST /api/fastapi/auth/signup with the form payload.
 */
export default function SignUpForm() {
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirm: '',
    organization: '',
    position: '',
    display_name: '',
    intent: '',
    preferred_name: '',
    honorific_prefix: '',
    post_nominals: '',
    job_title: '',
    orcid_id: '',
    affiliations: '',
  });
  const [feedback, setFeedback] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const feedbackRef = useRef(null);
  const isSignupCompleted = feedback?.type === 'success';

  useEffect(() => {
    if (!feedbackRef.current || !feedback?.message) return;
    feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    feedbackRef.current.focus({ preventScroll: true });
  }, [feedback]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);

    if (form.password !== form.confirm) {
      setFeedback({ type: 'warning', message: 'Passwords do not match.' });
      return;
    }

    if (!form.display_name.trim()) {
      setFeedback({ type: 'warning', message: 'Please provide your display name.' });
      return;
    }
    if (!form.organization.trim() || !form.position.trim()) {
      setFeedback({
        type: 'warning',
        message: 'Please provide your organization and your position (role within the organization).',
      });
      return;
    }
    if (!gdprAccepted) {
      setFeedback({ type: 'warning', message: 'Please accept the data processing terms to continue.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const postNominalsArr = (form.post_nominals || '')
        .split(/[,\n]/)
        .map(s => s.trim())
        .filter(Boolean);
      const affiliationsArr = (form.affiliations || '')
        .split(/[,\n]/)
        .map(s => s.trim())
        .filter(Boolean);
      const postNominalsStr = postNominalsArr.join(', ');

      const payload = {
        email: form.email,
        password: form.password,
        organization: form.organization.trim(),
        position: form.position.trim(),
        intent: form.intent.trim() || undefined,
        display_name: form.display_name.trim(),
        preferred_name: (form.preferred_name || '').trim() || undefined,
        honorific_prefix: (form.honorific_prefix || '').trim() || undefined,
        post_nominals: postNominalsStr || undefined,
        job_title: (form.job_title || '').trim() || undefined,
        orcid_id: (form.orcid_id || '').trim() || undefined,
        affiliations: affiliationsArr.length ? affiliationsArr : undefined,
      };

      const resp = await fetch('/api/fastapi/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        setFeedback({
          type: 'success',
          message: 'Confirmation email sent. Please verify your email address before signing in.',
          showLoginLink: true,
        });
        setForm({
          email: '',
          password: '',
          confirm: '',
          organization: '',
          position: '',
          display_name: '',
          intent: '',
          preferred_name: '',
          honorific_prefix: '',
          post_nominals: '',
          job_title: '',
          orcid_id: '',
          affiliations: '',
        });
        setGdprAccepted(false);
      } else {
        const detail = await resp.json().catch(() => null);
        const showLoginLink = isDuplicateEmailSignupError(resp.status, detail);
        setFeedback({
          type: 'error',
          message: getSignupErrorMessage(resp.status, detail),
          showLoginLink,
        });
      }
    } catch (err) {
      console.error(err);
      setFeedback({ type: 'error', message: toMessage(err) || 'Network error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="form-card">
        <h2 className="form-title">Create Your Small Airway Atlas Account</h2>
        {!isSignupCompleted && (
          <p className="form-subtitle">
            <p>
              <center>
                Small Airway Atlas, a meeting point for molecular data from the small airways.
              </center>
            </p>
            <p>
              <center>
                Explore curated datasets of proteins, lipids and miRNA detected in PEx samples and contribute to the growing knowledge base of distal lung biology.
              </center>
            </p>
            <p>
              <center>
                Please enter your details below. Fields marked with <span className="required-asterisk" aria-hidden="true">*</span> are required.
              </center>
            </p>
          </p>
        )}

        {feedback?.message && (
          <div
            ref={feedbackRef}
            className={`alert ${feedback.type === 'success' ? 'alert-success alert-success-spacing' : feedback.type === 'warning' ? 'alert-warning' : 'alert-error'}`}
            role="alert"
            aria-live="polite"
            tabIndex={-1}
          >
            <strong className="alert-title">
              {feedback.type === 'success'
                ? 'Confirmation email sent'
                : feedback.type === 'warning'
                  ? 'Please review the form'
                  : 'Sign-up could not be completed'}
            </strong>
            {toMessage(feedback.message)}
            {feedback.showLoginLink && (
              <>
                {' '}
                <a href="/api/fastapi/auth/login">Go to sign in</a>.
              </>
            )}
          </div>
        )}

        {!isSignupCompleted && (
          <form className="signup-form" onSubmit={handleSubmit} noValidate>

          <div className="form-group">
            <label className="form-label" htmlFor="display_name">
              Full name (as you’d like it shown) <span className="required-asterisk" aria-hidden="true">*</span>
            </label>
            <input
              id="display_name"
              className="form-input"
              required
              type="text"
              name="display_name"
              value={form.display_name}
              onChange={handleChange}
              aria-describedby="display-name-help"
            />
            <div id="display-name-help" className="help-text">Example: Prof. Alex Q. Smith, PhD</div>
          </div>
        
          <div className="form-row">
            <div className="form-group half">
              <label className="form-label" htmlFor="honorific_prefix">Honorific prefix (optional)</label>
              <input
                id="honorific_prefix"
                className="form-input"
                type="text"
                name="honorific_prefix"
                value={form.honorific_prefix}
                onChange={handleChange}
                placeholder="e.g., Dr., Prof."
              />
              <div id="honorific-prefix-help" className="help-text">Comma or newline separated (e.g., Dr., Prof.).</div>
            </div>

            <div className="form-group half">
              <label className="form-label" htmlFor="post_nominals">Post-nominals (optional)</label>
              <input
                id="post_nominals"
                className="form-input"
                type="text"
                name="post_nominals"
                value={form.post_nominals}
                onChange={handleChange}
                placeholder="e.g., PhD, MD, MSc"
                aria-describedby="post-nominals-help"
              />
              <div id="post-nominals-help" className="help-text">Comma or newline separated (e.g., PhD, MD).</div>
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label" htmlFor="intent">
              Intent for using Small Airway Atlas (optional)
            </label>
            <textarea
              id="intent"
              className="form-textarea"
              name="intent"
              value={form.intent}
              onChange={handleChange}
              aria-describedby="intent-help"
            />
            <div id="intent-help" className="help-text">Please, briefly describe how you'd like to use PExA's Small Airway Atlas.</div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="email">
              Email <span className="required-asterisk" aria-hidden="true">*</span>
            </label>
            <input
              id="email"
              className="form-input"
              required
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              aria-describedby="email-help"
            />
            <div id="email-help" className="help-text">This is the email your account will be created for.</div>
          </div>

          <div className="form-divider" />

          <div className="form-row">
            
            <div className="form-group half">
              <label className="form-label" htmlFor="position">
                Position (your role in the organization) <span className="required-asterisk" aria-hidden="true">*</span>
              </label>
              <input
                id="position"
                className="form-input"
                required
                type="text"
                name="position"
                value={form.position}
                onChange={handleChange}
                aria-describedby="position-help"
                placeholder="e.g., Professor, Postdoc, Senior Scientist"
              />
              <div id="position-help" className="help-text">
                  Your professional or academic position or role within your organization.
              </div>
            </div>
          
            <div className="form-group half">
              <label className="form-label" htmlFor="organization">
                Organization <span className="required-asterisk" aria-hidden="true">*</span>
              </label>
              <input
                id="organization"
                className="form-input"
                required
                type="text"
                name="organization"
                value={form.organization}
                onChange={handleChange}
                aria-describedby="organization-help"
              />
              <div id="organization-help" className="help-text">Your institution or company.</div>
            </div>

          </div>

          <div className="form-group" hidden>
            <label className="form-label" htmlFor="affiliations">Affiliations (Optional)</label>
            <textarea
              id="affiliations"
              className="form-textarea"
              name="affiliations"
              value={form.affiliations}
              onChange={handleChange}
              placeholder="e.g., Department of Pulmonology; Lab XYZ; University ABC"
              aria-describedby="affiliations-help"
            />
            <div id="affiliations-help" className="help-text">Comma or newline separated.</div>
          </div>

          <div className="form-group" hidden>
            <label className="form-label" htmlFor="orcid_id">ORCID iD (Optional)</label>
            <input
              id="orcid_id"
              className="form-input"
              type="text"
              name="orcid_id"
              value={form.orcid_id}
              onChange={handleChange}
              placeholder="e.g., 0000-0002-1825-0097"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">
              Password <span className="required-asterisk" aria-hidden="true">*</span>
            </label>
            <input
              id="password"
              className="form-input"
              required
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              aria-describedby="password-help"
            />
            <div id="password-help" className="help-text">Use a strong password with at least 8 characters.</div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirm">
              Confirm Password <span className="required-asterisk" aria-hidden="true">*</span>
            </label>
            <input
              id="confirm"
              className="form-input"
              required
              type="password"
              name="confirm"
              value={form.confirm}
              onChange={handleChange}
              aria-describedby="confirm-help"
            />
            <div id="confirm-help" className="help-text">Re-enter the same password for verification.</div>
          </div>

          <div className="form-divider" />

          <div className="form-group">
            <label className="form-label" htmlFor="gdpr-notice">
              GDPR and data processing <span className="required-asterisk" aria-hidden="true">*</span>
            </label>
            <div id="gdpr-notice" className="gdpr-box" role="region" aria-label="GDPR data processing notice">
              <p>
                To create and administer your Small Airway Atlas account, PExA AB processes the personal data you provide in this form (such as name, email address, organization, role, and optional profile details).
              </p>
              <p>
                The data is processed for account management, security, and communication related to the service. Processing is carried out in accordance with applicable data protection legislation, including the EU General Data Protection Regulation (GDPR).
              </p>
              <p>
                Your personal data is retained only as long as necessary to provide the service or as required by law. You have the right to request access, correction, or deletion of your personal data as described in our Privacy Policy.
              </p>
              <p>
                By selecting the checkbox below, you confirm that you have read and accept this data processing notice.
              </p>
            </div>
            <label className="checkbox-row" htmlFor="gdpr_accept">
              <input
                id="gdpr_accept"
                type="checkbox"
                checked={gdprAccepted}
                onChange={(e) => setGdprAccepted(e.target.checked)}
              />
              <span>I have read and accept the data processing notice above.</span>
            </label>
          </div>

          <div className="form-group" hidden>
            <label className="form-label" htmlFor="job_title">Job title (formal) (Optional)</label>
            <input
              id="job_title"
              className="form-input"
              type="text"
              name="job_title"
              value={form.job_title}
              onChange={handleChange}
              placeholder='e.g., “Associate Professor of Pulmonology”'
            />
          </div>

          <div className="form-group" hidden>
            <label className="form-label" htmlFor="preferred_name">Preferred name (Optional)</label>
            <input
              id="preferred_name"
              className="form-input"
              type="text"
              name="preferred_name"
              value={form.preferred_name}
              onChange={handleChange}
              placeholder="e.g., Alex"
              aria-describedby="preferred-name-help"
            />
            <div id="preferred-name-help" className="help-text">Used for greetings and casual UI.</div>
          </div>


          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Signing up…' : 'Sign Up to Small Airway Atlas'}
            </button>
          </div>
          </form>
        )}

        {!isSignupCompleted && (
          <p className="form-alt">
            Already have an account? <a className="link" href="/login">Log in</a>
          </p>
        )}
      </div>
    </div>
  );
}
